import React, { useState, useEffect } from 'react';
import adminApiService from '../../services/adminApiService';
import toast from 'react-hot-toast';
import {
  EnvelopeIcon,
  PhoneIcon,
  MagnifyingGlassIcon,
  EyeIcon,
  CheckCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  UserIcon,
  CalendarDaysIcon,
  ChatBubbleLeftRightIcon
} from '@heroicons/react/24/outline';

interface ContactInquiry {
  id: string;
  inquiryNumber: string;
  name: string;
  email: string;
  phone?: string;
  subject: string;
  message: string;
  status: 'open' | 'in-progress' | 'resolved' | 'closed';
  source: string;
  createdAt: any;
  updatedAt: any;
  responseCount: number;
  assignedTo?: {
    id: string;
    name: string;
  } | null;
}

interface ContactStats {
  total: number;
  open: number;
  inProgress: number;
  resolved: number;
  closed: number;
  avgResponseTime: number;
  bySource: {
    contact_form: number;
    email: number;
    phone: number;
  };
}

const ContactInquiries: React.FC = () => {
  const [inquiries, setInquiries] = useState<ContactInquiry[]>([]);
  const [stats, setStats] = useState<ContactStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedInquiry, setSelectedInquiry] = useState<ContactInquiry | null>(null);
  const [showInquiryModal, setShowInquiryModal] = useState(false);
  const [filters, setFilters] = useState({
    status: 'all',
    assignedTo: 'all',
    search: ''
  });
  const [responseText, setResponseText] = useState('');
  const [responseLoading, setResponseLoading] = useState(false);

  useEffect(() => {
    fetchInquiries();
    fetchStats();
  }, [filters]);

  const fetchInquiries = async () => {
    try {
      setLoading(true);
      const params: any = {};
      if (filters.status !== 'all') params.status = filters.status;
      if (filters.assignedTo !== 'all') params.assignedTo = filters.assignedTo;
      if (filters.search) params.search = filters.search;

      const response = await adminApiService.getContactInquiries(params);
      if (response.success) {
        setInquiries(response.data);
      }
    } catch (error: any) {
      console.error('Failed to fetch contact inquiries:', error);
      toast.error('Failed to load contact inquiries');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await adminApiService.getContactInquiryStats();
      if (response.success) {
        setStats(response.data);
      }
    } catch (error: any) {
      console.error('Failed to fetch contact stats:', error);
    }
  };

  const updateInquiryStatus = async (inquiryId: string, status: string) => {
    try {
      const response = await adminApiService.updateContactInquiryStatus(inquiryId, status);
      if (response.success) {
        toast.success('Status updated successfully');
        fetchInquiries();
        if (selectedInquiry) {
          setSelectedInquiry({ ...selectedInquiry, status: status as any });
        }
      }
    } catch (error: any) {
      console.error('Failed to update inquiry status:', error);
      toast.error('Failed to update status');
    }
  };

  const sendResponse = async () => {
    if (!selectedInquiry || !responseText.trim()) {
      toast.error('Please enter a response message');
      return;
    }

    try {
      setResponseLoading(true);
      const response = await adminApiService.respondToContactInquiry(selectedInquiry.id, {
        message: responseText,
        newStatus: 'resolved'
      });

      if (response.success) {
        toast.success('Response sent successfully');
        setResponseText('');
        setShowInquiryModal(false);
        fetchInquiries();
      }
    } catch (error: any) {
      console.error('Failed to send response:', error);
      toast.error('Failed to send response');
    } finally {
      setResponseLoading(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'open':
        return <ExclamationTriangleIcon className="h-4 w-4 text-yellow-500" />;
      case 'in-progress':
        return <ClockIcon className="h-4 w-4 text-blue-500" />;
      case 'resolved':
        return <CheckCircleIcon className="h-4 w-4 text-green-500" />;
      case 'closed':
        return <CheckCircleIcon className="h-4 w-4 text-gray-500" />;
      default:
        return <ClockIcon className="h-4 w-4 text-gray-400" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'open':
        return 'bg-yellow-100 text-yellow-800';
      case 'in-progress':
        return 'bg-blue-100 text-blue-800';
      case 'resolved':
        return 'bg-green-100 text-green-800';
      case 'closed':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (date: any) => {
    if (!date) return 'N/A';
    const d = date.toDate ? date.toDate() : new Date(date);
    return d.toLocaleDateString() + ' ' + d.toLocaleTimeString();
  };

  if (loading && !stats) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Contact Inquiries</h1>
        <p className="mt-1 text-sm text-gray-600">
          Manage and respond to customer contact inquiries
        </p>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <EnvelopeIcon className="h-6 w-6 text-gray-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Total Inquiries</dt>
                    <dd className="text-lg font-medium text-gray-900">{stats.total}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <ExclamationTriangleIcon className="h-6 w-6 text-yellow-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Open</dt>
                    <dd className="text-lg font-medium text-gray-900">{stats.open}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <ClockIcon className="h-6 w-6 text-blue-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">In Progress</dt>
                    <dd className="text-lg font-medium text-gray-900">{stats.inProgress}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <CheckCircleIcon className="h-6 w-6 text-green-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Resolved</dt>
                    <dd className="text-lg font-medium text-gray-900">{stats.resolved}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white shadow rounded-lg mb-6">
        <div className="px-4 py-5 sm:p-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select
                value={filters.status}
                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
              >
                <option value="all">All Status</option>
                <option value="open">Open</option>
                <option value="in-progress">In Progress</option>
                <option value="resolved">Resolved</option>
                <option value="closed">Closed</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Assignment</label>
              <select
                value={filters.assignedTo}
                onChange={(e) => setFilters({ ...filters, assignedTo: e.target.value })}
                className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
              >
                <option value="all">All Assignments</option>
                <option value="unassigned">Unassigned</option>
                <option value="me">Assigned to Me</option>
              </select>
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  value={filters.search}
                  onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                  className="block w-full pl-10 border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  placeholder="Search by name, email, subject, or reference number..."
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Inquiries Table */}
      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        <ul className="divide-y divide-gray-200">
          {loading ? (
            <li className="px-6 py-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto"></div>
              <p className="mt-2 text-sm text-gray-500">Loading inquiries...</p>
            </li>
          ) : inquiries.length === 0 ? (
            <li className="px-6 py-8 text-center">
              <EnvelopeIcon className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No inquiries found</h3>
              <p className="mt-1 text-sm text-gray-500">No contact inquiries match your current filters.</p>
            </li>
          ) : (
            inquiries.map((inquiry) => (
              <li key={inquiry.id}>
                <div className="px-4 py-4 sm:px-6 hover:bg-gray-50">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center min-w-0 flex-1">
                      <div className="flex-shrink-0">
                        <div className="h-10 w-10 bg-gray-100 rounded-full flex items-center justify-center">
                          <UserIcon className="h-5 w-5 text-gray-600" />
                        </div>
                      </div>
                      <div className="ml-4 min-w-0 flex-1">
                        <div className="flex items-center">
                          <p className="text-sm font-medium text-indigo-600 truncate">
                            {inquiry.inquiryNumber}
                          </p>
                          <span className={`ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(inquiry.status)}`}>
                            {getStatusIcon(inquiry.status)}
                            <span className="ml-1 capitalize">{inquiry.status}</span>
                          </span>
                        </div>
                        <div className="mt-1">
                          <p className="text-sm font-medium text-gray-900">{inquiry.name}</p>
                          <p className="text-sm text-gray-500">{inquiry.email}</p>
                        </div>
                        <div className="mt-1">
                          <p className="text-sm text-gray-900 font-medium">{inquiry.subject}</p>
                          <p className="text-sm text-gray-500 truncate">{inquiry.message}</p>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="text-right text-sm text-gray-500">
                        <div className="flex items-center">
                          <CalendarDaysIcon className="h-4 w-4 mr-1" />
                          {formatDate(inquiry.createdAt)}
                        </div>
                        {inquiry.phone && (
                          <div className="flex items-center mt-1">
                            <PhoneIcon className="h-4 w-4 mr-1" />
                            {inquiry.phone}
                          </div>
                        )}
                      </div>
                      <button
                        onClick={() => {
                          setSelectedInquiry(inquiry);
                          setShowInquiryModal(true);
                        }}
                        className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                      >
                        <EyeIcon className="h-4 w-4 mr-1" />
                        View
                      </button>
                    </div>
                  </div>
                </div>
              </li>
            ))
          )}
        </ul>
      </div>

      {/* Inquiry Detail Modal */}
      {showInquiryModal && selectedInquiry && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setShowInquiryModal(false)}></div>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="text-lg leading-6 font-medium text-gray-900">
                      Contact Inquiry Details
                    </h3>
                    <p className="mt-1 text-sm text-gray-500">
                      Reference: {selectedInquiry.inquiryNumber}
                    </p>
                  </div>
                  <button
                    onClick={() => setShowInquiryModal(false)}
                    className="rounded-md text-gray-400 hover:text-gray-600 focus:outline-none"
                  >
                    <span className="sr-only">Close</span>
                    <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Contact Information */}
                  <div>
                    <h4 className="text-sm font-medium text-gray-900 mb-3">Contact Information</h4>
                    <dl className="space-y-2">
                      <div>
                        <dt className="text-xs font-medium text-gray-500">Name</dt>
                        <dd className="text-sm text-gray-900">{selectedInquiry.name}</dd>
                      </div>
                      <div>
                        <dt className="text-xs font-medium text-gray-500">Email</dt>
                        <dd className="text-sm text-gray-900">{selectedInquiry.email}</dd>
                      </div>
                      {selectedInquiry.phone && (
                        <div>
                          <dt className="text-xs font-medium text-gray-500">Phone</dt>
                          <dd className="text-sm text-gray-900">{selectedInquiry.phone}</dd>
                        </div>
                      )}
                      <div>
                        <dt className="text-xs font-medium text-gray-500">Subject</dt>
                        <dd className="text-sm text-gray-900">{selectedInquiry.subject}</dd>
                      </div>
                      <div>
                        <dt className="text-xs font-medium text-gray-500">Status</dt>
                        <dd className="text-sm">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(selectedInquiry.status)}`}>
                            {getStatusIcon(selectedInquiry.status)}
                            <span className="ml-1 capitalize">{selectedInquiry.status}</span>
                          </span>
                        </dd>
                      </div>
                      <div>
                        <dt className="text-xs font-medium text-gray-500">Created</dt>
                        <dd className="text-sm text-gray-900">{formatDate(selectedInquiry.createdAt)}</dd>
                      </div>
                    </dl>

                    {/* Status Actions */}
                    <div className="mt-6">
                      <h4 className="text-sm font-medium text-gray-900 mb-3">Update Status</h4>
                      <div className="space-y-2">
                        {['open', 'in-progress', 'resolved', 'closed'].map((status) => (
                          <button
                            key={status}
                            onClick={() => updateInquiryStatus(selectedInquiry.id, status)}
                            disabled={selectedInquiry.status === status}
                            className={`w-full text-left px-3 py-2 text-sm border rounded-md ${
                              selectedInquiry.status === status
                                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                : 'hover:bg-gray-50 text-gray-700'
                            }`}
                          >
                            <span className="capitalize">{status.replace('-', ' ')}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Message & Response */}
                  <div>
                    <h4 className="text-sm font-medium text-gray-900 mb-3">Message</h4>
                    <div className="bg-gray-50 rounded-md p-4 mb-6">
                      <p className="text-sm text-gray-700 whitespace-pre-wrap">{selectedInquiry.message}</p>
                    </div>

                    <h4 className="text-sm font-medium text-gray-900 mb-3">Send Response</h4>
                    <div className="space-y-4">
                      <textarea
                        value={responseText}
                        onChange={(e) => setResponseText(e.target.value)}
                        rows={6}
                        className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                        placeholder="Type your response to the customer..."
                      />
                      <div className="flex justify-end space-x-3">
                        <button
                          onClick={() => setResponseText('')}
                          className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                        >
                          Clear
                        </button>
                        <button
                          onClick={sendResponse}
                          disabled={responseLoading || !responseText.trim()}
                          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                        >
                          {responseLoading ? (
                            <>
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                              Sending...
                            </>
                          ) : (
                            <>
                              <ChatBubbleLeftRightIcon className="h-4 w-4 mr-2" />
                              Send Response
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContactInquiries;